// Place search for the create page: venues, businesses and points of interest
// ("volksbuhne", "beghain", "curry 36") as well as addresses and cities, with
// typos forgiven. Uses Photon (OpenStreetMap data, run by Komoot) and falls
// back to Mapbox if Photon can't be reached. No Google.
//
// Results lean towards where the person is: the last place they picked on
// this device (rounded to about 1 km, kept only on the device), otherwise
// Berlin. It only tips the ranking, so "volksbühne köln" still finds Cologne.

import { placeFromMapboxFeature } from './eventPlace';

const PHOTON_URL = 'https://photon.komoot.io/api/';
const MAPBOX_URL = 'https://api.mapbox.com/geocoding/v5/mapbox.places';
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const BERLIN = { lat: 52.52, lon: 13.405 };
const AREA_KEY = 'frolik_place_area';

const roundToKm = (value) => Math.round(value * 100) / 100; // ~1 km

export function searchArea() {
  try {
    const saved = JSON.parse(localStorage.getItem(AREA_KEY) || 'null');
    if (saved && Number.isFinite(saved.lat) && Number.isFinite(saved.lon)) return saved;
  } catch (error) {
    // Storage blocked or bad value: use the default.
  }
  return BERLIN;
}

// Remember roughly where the last picked place was, to rank the next search.
export function rememberPlace(place) {
  const [lon, lat] = (place && place.center) || [];
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
  try {
    localStorage.setItem(AREA_KEY, JSON.stringify({ lat: roundToKm(lat), lon: roundToKm(lon) }));
  } catch (error) {
    // Not saved; next search uses the default area.
  }
}

const join = (parts, separator = ', ') => parts.filter(Boolean).join(separator);

// Kinds of OpenStreetMap entries that are a city/area rather than a venue.
const AREA_TYPES = new Set(['city', 'town', 'village', 'hamlet', 'district', 'locality', 'county', 'state', 'country', 'suburb', 'borough', 'quarter', 'neighbourhood']);
// Duplicates of a venue that people don't mean (taxi ranks, transit stops named after it).
const NOISE_VALUES = new Set(['taxi', 'mobility_hub', 'bus_stop', 'platform', 'stop_position', 'charging_station', 'parking', 'parking_entrance', 'bicycle_parking']);

export function placeFromPhotonFeature(feature) {
  const p = feature.properties || {};
  const [lon, lat] = (feature.geometry && feature.geometry.coordinates) || [];
  const isArea = AREA_TYPES.has(p.type) || AREA_TYPES.has(p.osm_value);
  const isStreet = p.osm_key === 'highway' || p.type === 'street';
  const isHouse = p.type === 'house' && !p.name;

  const streetLine = join([p.street, p.housenumber], ' ');
  const cityLine = join([p.postcode, p.city || (isArea ? '' : p.county)], ' ');
  const city = isArea && ['city', 'town', 'village', 'hamlet'].includes(p.type || p.osm_value) ? p.name : p.city || '';

  let primary;
  let secondary;
  if (isArea) {
    primary = p.name;
    secondary = join([p.type === 'country' ? '' : p.state, p.country]);
  } else if (isHouse) {
    primary = streetLine;
    secondary = join([cityLine, p.country]);
  } else if (isStreet) {
    primary = p.name || p.street;
    secondary = join([cityLine, p.country]);
  } else {
    primary = p.name || streetLine;
    secondary = join([p.name ? streetLine : '', cityLine, p.country]);
  }

  // [minLon, maxLat, maxLon, minLat] → Mapbox-style [minLon, minLat, maxLon, maxLat]
  const bbox = Array.isArray(p.extent) && p.extent.length === 4 ? [p.extent[0], p.extent[3], p.extent[2], p.extent[1]] : null;

  return {
    id: `osm-${p.osm_type || ''}${p.osm_id || `${lat},${lon}`}`,
    label: join([primary, secondary]),
    primary: primary || '',
    secondary: secondary || '',
    center: [lon, lat],
    bbox,
    city: city || '',
    country: p.country || '',
    noise: NOISE_VALUES.has(p.osm_value),
  };
}

function withMapboxLines(place) {
  const [primary, ...rest] = String(place.label || '').split(', ');
  return { ...place, primary, secondary: rest.join(', ') };
}

// The same venue often appears more than once (main entry plus a taxi rank or
// a second node): keep the first of each name+street+city.
function dedupe(places) {
  const seen = new Set();
  return places.filter((place) => {
    const key = place.label.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

async function searchPhoton(query, { signal, limit }) {
  const area = searchArea();
  const params = new URLSearchParams({
    q: query,
    limit: String(limit * 2),
    lang: 'en',
    lat: String(area.lat),
    lon: String(area.lon),
  });
  const response = await fetch(`${PHOTON_URL}?${params}`, { signal });
  if (!response.ok) throw new Error(`Photon ${response.status}`);
  const data = await response.json();
  const places = (data.features || []).map(placeFromPhotonFeature).filter((place) => place.primary);
  const useful = places.filter((place) => !place.noise);
  return dedupe(useful.length ? useful : places).slice(0, limit);
}

async function searchMapbox(query, { signal, limit }) {
  if (!MAPBOX_TOKEN) return [];
  const area = searchArea();
  const url = `${MAPBOX_URL}/${encodeURIComponent(query)}.json?autocomplete=true&limit=${limit}` +
    `&proximity=${area.lon},${area.lat}&access_token=${MAPBOX_TOKEN}`;
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`Mapbox ${response.status}`);
  const data = await response.json();
  return (data.features || []).map((feature) => withMapboxLines(placeFromMapboxFeature(feature)));
}

const PHOTON_TIMEOUT_MS = 8000;

function withTimeout(promise, ms, signal) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms);
    if (signal) signal.addEventListener('abort', () => clearTimeout(timer));
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (error) => { clearTimeout(timer); reject(error); }
    );
  });
}

// Photon has the better venue results but can take a few seconds; Mapbox is
// instant but weak on venues. Both are asked at once: Mapbox's list shows
// first (pending: true), then Photon's replaces it. If Photon fails or is too
// slow, Mapbox's list stays.
export function searchPlaces(query, { signal, limit = 6, onResults }) {
  const text = String(query || '').trim();
  if (!text) {
    onResults([], { pending: false });
    return Promise.resolve();
  }

  let photonDone = false;
  let mapboxResults = null;

  const mapbox = searchMapbox(text, { signal, limit })
    .then((places) => {
      mapboxResults = places;
      if (!photonDone && !(signal && signal.aborted)) onResults(places, { pending: true });
    })
    .catch(() => {
      mapboxResults = [];
    });

  const photon = withTimeout(searchPhoton(text, { signal, limit }), PHOTON_TIMEOUT_MS, signal)
    .then((places) => {
      photonDone = true;
      if (!(signal && signal.aborted)) onResults(places, { pending: false });
    })
    .catch(async (error) => {
      photonDone = true;
      if (error.name === 'AbortError' || (signal && signal.aborted)) return;
      await mapbox;
      onResults(mapboxResults || [], { pending: false });
    });

  return Promise.all([mapbox, photon]);
}
